'use client';

import React from 'react';
import dynamic from 'next/dynamic';
import { MarketType } from '@velocity-exchange/sdk';
import { useTradePageLayout } from 'src/hooks/useTradePageLayout/useTradePageLayout';
import { TRADE_AREAS } from 'src/hooks/useTradePageLayout/type';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useCurrentSettings from 'src/hooks/useCurrentSettings';
import { useDisplayStalePageWarning } from '../../hooks/useDisplayStalePageWarning';
import MarketBar from '../MarketBar/MarketBar';
import MarketSelector from 'src/components/MarketSelector/MarketSelector';
import OrderbookAndRecentTrades from '../OrderbookAndRecentTrades';
import { TradeForm } from '../TradeForm/TradeForm';
import MarginCalcPanel from 'src/components/MarginInfo/MarginCalcPanel';
import TradePageTable from '../TradePageTable/TradePageTable';
import DraggableTableResizer from '../DraggableTableResizer';
import MobileMainPage from './mobile/MobileMainPage';
import GridSectionWrapper from 'src/components/GridSections/GridSectionWrapper/GridSectionWrapper';
import { useWindowSizeIsLessThan } from 'src/hooks/useWindowSize';
import InlineAlert from 'src/components/primitives/InlineAlert/InlineAlert';
import { useIsolatedLowHealthWarning } from 'src/hooks/useLowHealthWarning';
import HiddenMarketBanner from 'src/components/StatusBars/HiddenMarketBanner';
import type { TVChartContainerProps } from 'src/components/TradingView/types';

const TVChartContainer = dynamic(
	() =>
		import('../TradingView').then(
			(mod) =>
				mod.TVChartContainer as React.ComponentType<TVChartContainerProps>
		),
	{ ssr: false }
);

const TradePageGrid = () => {
	const isMobile = useWindowSizeIsLessThan('sm');
	const [settings] = useCurrentSettings();
	const showMarketBar = settings.showMarketBar !== false;
	const showOrderbook = isMobile || settings.showOrderbook !== false;
	const layoutStyles = useTradePageLayout(showMarketBar, showOrderbook);
	const selectedMarketType = useDriftStore(
		(s) => s.selectedMarket.current?.marketType
	);
	const isolatedLowHealthWarning = useIsolatedLowHealthWarning(20);

	useDisplayStalePageWarning();

	if (isMobile) {
		return <MobileMainPage />;
	}

	return (
		<div
			className="grid h-full w-full gap-xs p-xs xl:p-sm 2xl:gap-sm"
			style={{
				gridTemplateAreas: layoutStyles.gridTemplateAreas,
				gridTemplateColumns: layoutStyles.gridTemplateColumns,
				gridTemplateRows: layoutStyles.gridTemplateRows,
			}}
		>
			{/* Market Bar (Favorites) */}
			{showMarketBar && (
				<div style={{ gridArea: TRADE_AREAS.MarketBar }}>
					<GridSectionWrapper
						noPad
						className="relative overflow-visible rounded-sm border-none"
					>
						<MarketBar />
					</GridSectionWrapper>
				</div>
			)}

			{/* Market Selector (Stats) */}
			<div style={{ gridArea: TRADE_AREAS.MarketSelector }}>
				<MarketSelector />
			</div>

			{/* Chart */}
			<div
				style={{ gridArea: TRADE_AREAS.Chart }}
				className="relative min-h-0 min-w-0"
			>
				<HiddenMarketBanner className="mb-1" />
				<GridSectionWrapper
					noPad
					className="flex flex-col rounded-sm border-none"
				>
					<div className="relative h-full w-full">
						<TVChartContainer />
					</div>
				</GridSectionWrapper>
			</div>

			{/* Orderbook */}
			{showOrderbook && (
				<div
					style={{ gridArea: TRADE_AREAS.Orderbook }}
					className="relative min-h-0 min-w-0"
				>
					<OrderbookAndRecentTrades />
				</div>
			)}

			{/* Trade Form + Cross Margin Panel */}
			<div
				style={{ gridArea: TRADE_AREAS.TradeForm }}
				className="flex min-h-0 min-w-0 flex-col gap-sm"
			>
				<GridSectionWrapper
					noPad
					className="flex flex-col justify-between gap-xs overflow-visible border-none bg-transparent md:justify-start md:rounded-sm 2xl:gap-sm"
				>
					<div className="h-auto flex-1 rounded-sm bg-container-bg md:flex-0 vertical:flex-1">
						<TradeForm
							marketType={selectedMarketType as MarketType}
							className="h-auto"
						/>
					</div>
					<div className="relative hidden-scroll w-full overflow-auto rounded-sm bg-container-bg">
						{isolatedLowHealthWarning && (
							<div className="p-md">
								<InlineAlert
									variant="warning"
									title={isolatedLowHealthWarning?.message}
								/>
							</div>
						)}
						<MarginCalcPanel />
					</div>
				</GridSectionWrapper>
			</div>

			{/* Table */}
			<div
				style={{ gridArea: TRADE_AREAS.Table }}
				className="relative min-h-0 min-w-0"
			>
				<DraggableTableResizer />
				<GridSectionWrapper
					noPad
					className="h-full overflow-visible rounded-sm border-none"
				>
					<TradePageTable />
				</GridSectionWrapper>
			</div>
		</div>
	);
};

export default TradePageGrid;
